import {
  EditorTypeEnum,
  IUserInfo,
  IWorkUser,
  makeNameInitials,
  RichTextEditor,
  stringToHash,
  UserInitialsColorsMap,
} from '@diana/core';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { Box, Button, CircularProgress, Stack, useTheme } from '@mui/material';
import { useCallback, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { RichTextEditorBox } from './Comments.styles';
import { userMentionCustomisation } from './Support';

export interface ICommentsRichTextEditorProps {
  users: IWorkUser[];
  data?: string;
  sendButton?: {
    fn: (arg: string) => void;
    label: string;
    isLoading: boolean;
  };
  cancelButton?: {
    fn: () => void;
    label: string;
  };
  placeholder?: string;
  editMode?: boolean;
}

export interface ICommentsRichTextEditorRef {
  clearTextEditor: () => void;
}

const TextEditor: React.ForwardRefRenderFunction<
  ICommentsRichTextEditorRef,
  ICommentsRichTextEditorProps
> = (props: ICommentsRichTextEditorProps, forwardedRef) => {
  const { placeholder, cancelButton, sendButton, users, data, editMode } = props;

  const usersFeedRef = useRef<IWorkUser[]>([]);
  const editorInstance = useRef<any>(null);
  const { palette } = useTheme();
  const themeMode = palette.mode ?? 'dark';
  const getTextColor = UserInitialsColorsMap[themeMode].getTextColor;
  const getBackgroundColor = UserInitialsColorsMap[themeMode].getBackgroundColor;
  const [value, setValue] = useState<any>(null);

  useImperativeHandle(forwardedRef, () => ({
    clearTextEditor() {
      editorInstance.current?.setData('');
    },
  }));

  const isDisabled = !value || value === data;

  useEffect(() => {
    usersFeedRef.current = users.map((user) => ({
      ...user,
      uid: user.id,
      id: `@${user.displayName}`,
    }));
  }, [users]);

  //set focus in the end of sentence in edit mode
  useEffect(() => {
    if (editMode)
      setTimeout(() => {
        editorInstance.current?.editing.view.focus();
        editorInstance.current?.ui.view.element.classList.add('custom-cursor');
        editorInstance.current.model.change((writer: any) => {
          writer.setSelection(editorInstance.current.model.document.getRoot(), 'end');
        });
      }, 500);
  }, [editMode]);

  const handleSendAction = () => {
    sendButton!.fn(editorInstance.current?.getData());
  };

  const customItemRenderer = useCallback(
    (user: IUserInfo) => {
      const altHash = stringToHash(user.name || '?');
      const img = document.createElement(user.imageUrl ? 'img' : 'div');
      img.innerText = makeNameInitials(user.name || '?', 2);
      img.classList.add('custom-mention-img');
      // @ts-ignore
      img.src = user.imageUrl || '-';
      // @ts-ignore
      img.alt = user.imageUrl || '   ';
      img.style.cssText = `
       color: ${getTextColor(altHash) + '!important'};
       padding: ${user.imageUrl ? 'none' : '3px!important'};
       background-color:${getBackgroundColor(altHash) + '!important'};
      `;

      const name = document.createElement('p');
      name.textContent = user.name || '';
      name.classList.add('custom-mention-name');

      const email = document.createElement('div');
      email.textContent = user.email || '';
      email.classList.add('custom-mention-email');

      const info = document.createElement('div');
      info.style.cssText = 'display:flex, flex-direction:column;';
      info.appendChild(name);
      info.appendChild(email);

      const wrapper = document.createElement('div');
      wrapper.appendChild(img);
      wrapper.appendChild(info);
      wrapper.style.cssText = 'display:flex; align-items: center';
      return wrapper;
    },
    [getTextColor, getBackgroundColor],
  );

  return (
    <RichTextEditorBox
      id={editMode ? 'editMode' : ''}
      sx={{
        borderTop: (theme) => (!editMode ? `1px solid ${theme.palette.divider}` : 'none'),
        borderRadius: '9px 8px 0 0',
        flexDirection: 'column',
        '.ck.ck-editor': {
          width: '100%',
        },
        '.ck.ck-sticky-panel': {
          display: 'none',
        },
      }}
    >
      <RichTextEditor
        disabled={sendButton!?.isLoading}
        setValue={setValue}
        placeholder={placeholder}
        editor={EditorTypeEnum.CLASSIC}
        config={{
          mention: {
            feeds: [
              {
                marker: '@',
                minimumCharacters: 0,
                feed: () => {
                  // this is to control the visibility of mentions list
                  // RichTextEditorBox sx prop could not control the mention classes because it is outside of its scope
                  return Promise.resolve(
                    // using ref here. for some reason this function could not get the latest inputMode state
                    usersFeedRef.current,
                  );
                },
                itemRenderer: customItemRenderer,
              },
            ],
          },
        }}
        onReady={(editor: any) => {
          editorInstance.current = editor;
          if (editorInstance.current) {
            userMentionCustomisation({ editorInstance: editorInstance.current });

            if (data) editorInstance.current.setData(data);
          }
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          width: '100%',
          borderTop: `1px solid ${palette.border.main}`,
          height: 52,
          px: 2,
          backgroundColor: palette.background.paperLowElevation,
        }}
      >
        <Box sx={{ flex: 1 }} />
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ backgroundColor: palette.background.paperLowElevation }}
        >
          {cancelButton && (
            <Button variant="contained" color="secondary" onClick={cancelButton.fn}>
              {cancelButton.label}
            </Button>
          )}
          {sendButton && (
            <Button variant="contained" onClick={handleSendAction} disabled={isDisabled}>
              {sendButton.label}
              {sendButton.isLoading ? (
                <CircularProgress
                  size="18px"
                  sx={{
                    ml: 1,
                    color: palette.getContrastText(palette.primary.main),
                  }}
                />
              ) : (
                !editMode && (
                  <SendRoundedIcon
                    sx={{
                      ml: 1,
                      fontSize: '16px',
                      color: isDisabled
                        ? palette.text.disabled
                        : palette.getContrastText(palette.primary.main),
                    }}
                  />
                )
              )}
            </Button>
          )}
        </Stack>
      </Stack>
    </RichTextEditorBox>
  );
};

export const CommentsRichTextEditor = forwardRef(TextEditor);
